<template>
  <d-page-layout :hasSearch="false">
    <template slot="heading">
      <v-breadcrumbs class="pa-0" :items="items"></v-breadcrumbs>
    </template>

    <template slot="content">
      <d-loading v-if="loading.page"></d-loading>
      <template v-else>
        <v-row>
          <v-col cols="12" md="4" class="mb-3">
            <h6 class="subtitle-1 font-weight-bold gray--text text--darken-2">
              {{ $t("users.add.form.username") }}:
            </h6>
            <p class="mb-0">{{ user?.username }}</p>
          </v-col>

          <v-col cols="12" md="4" class="mb-3">
            <h6 class="subtitle-1 font-weight-bold gray--text text--darken-2">
              {{ $t("users.add.form.organization") }}:
            </h6>
            <p class="mb-0">{{ user?.organizationName }}</p>
          </v-col>

          <v-col cols="12" md="4" class="mb-3">
            <h6 class="subtitle-1 font-weight-bold gray--text text--darken-2">
              {{ $t("users.add.form.user-type") }}:
            </h6>
            <p class="mb-0">{{ user?.userType?.name }}</p>
          </v-col>
        </v-row>

        <h3 class="text-h6 py-2 border-top">İcazələr</h3>

        <v-expansion-panels multiple accordion class="border-all">
          <v-expansion-panel
            class="elevation-0"
            v-for="(project, i) in userProjects"
            :key="i"
          >
            <v-expansion-panel-header class="border-bottom">
              <div>
                <h6 class="subtitle-1 font-weight-bold">
                  <v-chip label>
                    <v-avatar tile left color="primary" class="white--text">
                      {{ getAccessCount(project?.apis) }}
                    </v-avatar>
                    {{ project.projectOwner.displayText }}
                  </v-chip>

                  <span class="mx-2">{{ project.name }}</span>
                  <span class="body-2">{{ project.description }}</span>
                </h6>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="pt-4">
              <v-alert
                dense
                outlined
                :color="getMethodColor(api.httpMethodType.value)"
                v-for="(api, i) in project.apis"
                :key="i"
                :class="{ 'mb-0': project.apis.length - 1 === i }"
              >
                <v-row no-gutters align="center">
                  <v-col>
                    <div class="w-100 d-flex align-center">
                      <v-chip
                        label
                        small
                        :color="getMethodColor(api.httpMethodType.value)"
                        class="mr-3"
                        >{{ api.httpMethodType.displayText }}</v-chip
                      >

                      <p class="body-1 font-weight-bold mr-2 mb-0">
                        {{ api.name }}
                        <span class="mb-0 body-2 font-weight-regular">
                          {{ api.description }}
                        </span>
                      </p>
                    </div>
                  </v-col>
                  <v-col cols="auto">
                    <div class="d-flex justify-end">
                      <v-switch
                        v-model="api.isSelected"
                        :value="api.isSelected"
                        inset
                        hide-details
                        @click="approvePermission(api)"
                        readonly
                        class="mt-0"
                      ></v-switch>

                      <v-tooltip top class="ml-2">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            color="gray darken-2"
                            icon
                            @click="handleLogModal(api)"
                          >
                            <v-icon size="24"> mdi-history </v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("defaults.button.view-log") }}</span>
                      </v-tooltip>
                    </div>
                  </v-col>
                </v-row>
              </v-alert>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <d-confirm-modal
          v-if="selectedApi && modal"
          :modal="modal"
          :title="
            $t('users.api-change-status.title', {
              status: selectedApi.isSelected
                ? $t('defaults.text.inactive').toLowerCase()
                : $t('defaults.text.active').toLowerCase(),
            })
          "
          :text="
            $t('users.api-change-status.text', {
              status: selectedApi.isSelected
                ? $t('defaults.text.inactive').toLowerCase()
                : $t('defaults.text.active').toLowerCase(),
            })
          "
          :submitButtonText="
            selectedApi.isSelected
              ? $t('defaults.button.deactivate')
              : $t('defaults.button.activate')
          "
          :submitButtonColor="selectedApi.isSelected ? 'error' : 'success'"
          :isSubmitting="isSubmitting"
          @submitModal="handleSubmitModal"
          @closeModal="handleCloseModal"
        ></d-confirm-modal>

        <Logs
          v-if="selectedApi && logModal"
          :modal="logModal"
          :item="selectedApi"
          @closeModal="handleCloseLogModal"
        ></Logs>
      </template>
    </template>
  </d-page-layout>
</template>
  <script>
import { getUserById, getUserApis, changeUserApiStatus } from "@/services/user";
import HttpMethods from "@/enums/HttpMethods.js";
import Logs from "./Logs.vue";

export default {
  components: { Logs },
  data: (vm) => ({
    HttpMethods,
    items: [
      {
        text: vm.$t("users.page-heading"),
        disabled: false,
        href: "/users",
      },
      {
        text: "",
        disabled: true,
      },
    ],
    loading: {
      page: false,
    },
    user: null,
    userProjects: [],
    modal: false,
    isSubmitting: false,
    selectedApi: null,
    logModal: false,
  }),
  computed: {
    userId() {
      return this.$route.params.id;
    },
  },
  methods: {
    getAccessCount(apis) {
      const apisCount = apis?.length;
      const selectedApisCount = apis.filter((item) => item.isSelected)?.length;

      return `${apisCount}/${selectedApisCount}`;
    },
    async getUserById() {
      try {
        const { data } = await getUserById(this.userId);
        this.user = data;
      } catch {
        this.$eventBus.$emit("notification", {
          type: "error",
          message: this.$t("defaults.text.request-error"),
        });
      }
    },
    async getUserApis() {
      try {
        const response = await getUserApis(this.userId);
        this.userProjects = response;
      } catch {
        this.$eventBus.$emit("notification", {
          type: "error",
          message: this.$t("defaults.text.request-error"),
        });
      }
    },
    approvePermission(item) {
      this.selectedApi = item;
      this.modal = true;
    },
    async handleSubmitModal() {
      try {
        this.isSubmitting = true;
        await changeUserApiStatus({
          userId: this.userId,
          apiId: this.selectedApi.id,
          isActive: !this.selectedApi.isSelected,
        });
        this.$eventBus.$emit("notification", {
          type: "success",
          message: this.$t("users.api-change-status.alert.changed", {
            status: this.selectedApi.isSelected
              ? this.$t("defaults.text.inactive").toLowerCase()
              : this.$t("defaults.text.active").toLowerCase(),
          }),
        });
        await this.getUserApis();
        this.handleCloseModal();
      } catch {
        this.$eventBus.$emit("notification", {
          type: "error",
          message: this.$t("defaults.text.request-error"),
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    handleCloseModal() {
      this.modal = false;
      this.selectedApi = null;
    },
    handleCloseLogModal() {
      this.logModal = false;
      this.selectedApi = null;
    },
    handleLogModal(item) {
      this.logModal = true;
      this.selectedApi = item;
    },
  },
  async mounted() {
    try {
      this.loading.page = true;
      await this.getUserById();
      await this.getUserApis();
      this.items[1].text = this.user?.username;
    } finally {
      this.loading.page = false;
    }
  },
};
</script>
  